// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import { Document, Page, Text, View, Font, StyleSheet } from "@react-pdf/renderer";
import Roboto_Light from "../../../../resources/fonts/Roboto/Roboto-Light.ttf";
import Roboto_Bold from "../../../../resources/fonts/Roboto/Roboto-Bold.ttf";

Font.register({
  family: "Roboto",
  fonts: [
    { src: Roboto_Light },
    { src: Roboto_Bold, fontWeight: 700 },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

// ---------------------------------------------------------------------------
// Pure calculation helpers (ported from CreditScoreChecking.js)
// ---------------------------------------------------------------------------

function formatNumber(n) {
  return typeof n === "number" ? n : 0;
}

function checkObjectComplete(obj) {
  return Object.keys(obj).every((key) => obj[key] !== "");
}

function convertToDollars(obj) {
  let result = JSON.parse(JSON.stringify(obj));
  Object.keys(obj).forEach((key) => {
    if (!(key === "companyId" || key === "year" || key === "exchangeRate")) {
      result[key] =
        Math.round(
          (parseFloat(result[key]) / parseFloat(result["exchangeRate"])) * 100
        ) / 100;
    }
  });
  return result;
}

function fmt(n) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function calcCurrentRatio(y) {
  if (!checkObjectComplete(y)) return "";
  const v = formatNumber(
    Math.round((y.currentAssets / y.currentLiability) * 100) / 100
  );
  return isNaN(v) ? "-" : v;
}

function calcCashRatio(y) {
  if (!checkObjectComplete(y)) return "";
  const v = formatNumber(
    Math.round(((y.cash + y.investments) / y.currentLiability) * 100) / 100
  );
  return isNaN(v) ? "-" : v;
}

function calcWorkingCapitalRatio(y) {
  return formatNumber(
    Math.round((y.currentAssets - y.currentLiability) * 100) / 100
  );
}

function calcDebtRatio(y) {
  const v = formatNumber(
    Math.round((y.totalDebt / y.totalAssets) * 100) / 100
  );
  return isNaN(v) ? "-" : v;
}

function getRevenue(y) {
  return formatNumber(y.revenue);
}

function calcRevenueGrowth(yCurr, yPrev) {
  if (!yCurr || !yPrev) return "";
  const v = formatNumber(
    Math.round(
      ((yCurr.revenue - yPrev.revenue) / yPrev.revenue) * 10000
    ) / 100
  );
  return isNaN(v) ? "" : v;
}

function getProfit(y) {
  return formatNumber(y.profit);
}

function calcCurrentAssetYoY(yCurr, yPrev) {
  if (!yCurr || !yPrev) return "";
  const v = formatNumber(
    Math.round(
      ((yCurr.currentAssets - yPrev.currentAssets) / yPrev.currentAssets) * 10000
    ) / 100
  );
  return isNaN(v) ? "" : v;
}

function calcCurrentLiabilityYoY(yCurr, yPrev) {
  if (!yCurr || !yPrev) return "";
  const v = formatNumber(
    Math.round(
      ((yCurr.currentLiability - yPrev.currentLiability) /
        yPrev.currentLiability) *
        10000
    ) / 100
  );
  return isNaN(v) ? "" : v;
}

function getRatioScore(category, value, ratios) {
  const scaleArray = ratios.filter((r) => r.ratioCategory === category);
  let returnValue = "";
  scaleArray.forEach((scale, id) => {
    if (scale.minVal === null && value <= scale.maxVal) {
      returnValue = scale.creditScore.toString();
    } else if (scale.maxVal === null && value > scale.minVal) {
      returnValue = scale.creditScore.toString();
    } else if (value > scale.minVal && value <= scale.maxVal) {
      returnValue = scale.creditScore.toString();
    } else if (id === 0 && value === scale.minVal) {
      returnValue = scale.creditScore.toString();
    }
  });
  return returnValue || "0";
}

function getRatioForYear(category, y, ratios) {
  let value;
  if (category === "Current ratio") value = calcCurrentRatio(y);
  else if (category === "Cash ratio") value = calcCashRatio(y);
  else if (category === "WC") value = calcWorkingCapitalRatio(y);
  else if (category === "Debt ratio") value = calcDebtRatio(y);
  else if (category === "Revenue") value = getRevenue(y);
  else if (category === "Revenue growth") value = 0; // year1 baseline
  else return "0";
  return getRatioScore(category, value, ratios);
}

function getRatioForYearGrowth(category, yCurr, yPrev, ratios) {
  let value;
  if (category === "Revenue growth") value = calcRevenueGrowth(yCurr, yPrev);
  else return getRatioForYear(category, yCurr, ratios);
  return getRatioScore(category, value, ratios);
}

function calcTotalCreditScore(y1, y2, y3, ratios, yearNum) {
  const y = yearNum === 1 ? y1 : yearNum === 2 ? y2 : y3;
  const prev = yearNum === 2 ? y1 : yearNum === 3 ? y2 : null;
  let rgScore =
    yearNum === 1
      ? "0"
      : getRatioForYearGrowth("Revenue growth", y, prev, ratios);
  const result =
    parseInt(getRatioForYear("Current ratio", y, ratios)) +
    parseInt(getRatioForYear("Cash ratio", y, ratios)) +
    parseInt(getRatioForYear("WC", y, ratios)) +
    parseInt(getRatioForYear("Debt ratio", y, ratios)) +
    parseInt(getRatioForYear("Revenue", y, ratios)) +
    parseInt(rgScore);
  return isNaN(result) ? "-" : result;
}

function calcFinalTotalCreditScore(y1, y2, y3, ratios) {
  let latestYear = y1;
  let latestYearNum = 1;
  if (checkObjectComplete(y3)) { latestYear = y3; latestYearNum = 3; }
  else if (checkObjectComplete(y2)) { latestYear = y2; latestYearNum = 2; }

  const prevYear = latestYearNum === 3 ? y2 : latestYearNum === 2 ? y1 : null;
  const rgScore = latestYearNum === 1 || !prevYear
    ? "0"
    : getRatioForYearGrowth("Revenue growth", latestYear, prevYear, ratios);

  const result =
    parseInt(getRatioForYear("Current ratio", latestYear, ratios)) +
    parseInt(getRatioForYear("Cash ratio", latestYear, ratios)) +
    parseInt(getRatioForYear("WC", latestYear, ratios)) +
    parseInt(getRatioForYear("Debt ratio", latestYear, ratios)) +
    parseInt(getRatioForYear("Revenue", latestYear, ratios)) +
    parseInt(rgScore);
  return isNaN(result) ? "-" : result;
}

function calcCreditRating(totalScore, ratios) {
  const scaleArray = ratios.filter((r) => r.ratioCategory === "Rating scale");
  let returnValue = "";
  scaleArray.forEach((scale, id) => {
    if (scale.minVal === null && totalScore <= scale.maxVal) {
      returnValue = scale.creditScore.toString();
    } else if (scale.maxVal === null && totalScore > scale.minVal) {
      returnValue = scale.creditScore.toString();
    } else if (totalScore > scale.minVal && totalScore <= scale.maxVal) {
      returnValue = scale.creditScore.toString();
    } else if (id === 0 && totalScore === scale.minVal) {
      returnValue = scale.creditScore.toString();
    }
  });
  return returnValue;
}

// ---------------------------------------------------------------------------
// Status color helper
// ---------------------------------------------------------------------------

function statusColor(status) {
  if (!status) return "#E07030";
  const s = status.toLowerCase();
  if (s === "approved" || s === "active") return "#2E7D32";
  if (s === "rejected" || s === "inactive") return "#C62828";
  return "#E07030";
}

function normalizeStatus(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const s = raw.toString().trim().toLowerCase();
  if (s === "1" || s === "true" || s === "approved") return "Approved";
  if (s === "0" || s === "false" || s === "rejected") return "Rejected";
  if (s === "requested" || s === "pending") return "Pending";
  const trimmed = raw.toString().trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

// ---------------------------------------------------------------------------
// Style definitions
// ---------------------------------------------------------------------------

const NAVY = "#1C3453";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    paddingTop: 55,
    paddingBottom: 50,
    paddingHorizontal: 40,
    fontSize: 9,
    color: "#333",
  },
  // Section header bar
  sectionHeader: {
    backgroundColor: NAVY,
    padding: 8,
    marginBottom: 8,
    marginTop: 12,
  },
  sectionHeaderText: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 11,
  },
  // Sub-section header (bold + bottom border)
  subSectionHeader: {
    fontWeight: 700,
    fontSize: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    paddingBottom: 3,
    marginTop: 10,
    marginBottom: 6,
  },
  // Sub-sub-section header (smaller)
  subSubSectionHeader: {
    fontWeight: 700,
    fontSize: 9,
    marginTop: 8,
    marginBottom: 4,
  },
  // Data row
  dataRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#E8E8E8",
    paddingVertical: 4,
  },
  dataLabel: {
    width: "35%",
    fontWeight: 700,
    paddingRight: 4,
  },
  dataValue: {
    width: "65%",
  },
  // Table styles
  tableHeader: {
    flexDirection: "row",
    backgroundColor: NAVY,
    padding: 4,
  },
  tableHeaderCell: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#dddddd",
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  tableRowAlt: {
    backgroundColor: "#F5F5F5",
  },
  tableCell: {
    fontSize: 8,
  },
  // Comment card
  commentCard: {
    borderWidth: 0.5,
    borderColor: "#e0e0e0",
    padding: 8,
    marginBottom: 6,
  },
  commentUser: {
    fontWeight: 700,
    color: NAVY,
    marginBottom: 3,
  },
  commentText: {
    fontSize: 8,
    color: "#444",
  },
});

// ---------------------------------------------------------------------------
// Shared layout primitives
// ---------------------------------------------------------------------------

const PageHeader = ({ companyName, reportDate }) => (
  <View
    fixed
    style={{ position: "absolute", top: 15, left: 40, right: 40 }}
  >
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        paddingBottom: 6,
      }}
    >
      <Text style={{ fontWeight: 700, fontSize: 11, color: NAVY }}>
        {companyName}
      </Text>
      <Text style={{ fontSize: 9, color: "#555" }}>
        Due Diligence Report — {reportDate}
      </Text>
    </View>
    <View style={{ borderBottomWidth: 0.5, borderBottomColor: "#888" }} />
  </View>
);

const PageFooter = () => (
  <View
    fixed
    style={{ position: "absolute", bottom: 15, left: 40, right: 40 }}
  >
    <View
      style={{
        borderTopWidth: 0.5,
        borderTopColor: "#aaa",
        paddingTop: 4,
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <Text style={{ fontSize: 8, color: "#555" }}>
        WSO2 Due Diligence Report — Confidential
      </Text>
      <Text
        style={{ fontSize: 8, color: "#555" }}
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
      />
    </View>
  </View>
);

const SectionHeader = ({ children }) => (
  <View style={styles.sectionHeader}>
    <Text style={styles.sectionHeaderText}>{children}</Text>
  </View>
);

const SubSectionHeader = ({ children }) => (
  <Text style={styles.subSectionHeader}>{children}</Text>
);

const SubSubSectionHeader = ({ children }) => (
  <Text style={styles.subSubSectionHeader}>{children}</Text>
);

const DataRow = ({ label, value }) => (
  <View style={styles.dataRow}>
    <Text style={styles.dataLabel}>{label}</Text>
    <Text style={styles.dataValue}>{value || "—"}</Text>
  </View>
);

const CommentCard = ({ item }) => (
  <View style={styles.commentCard}>
    <Text style={styles.commentUser}>{item.userName}</Text>
    <Text style={styles.commentText}>{item.comment}</Text>
  </View>
);

// ---------------------------------------------------------------------------
// Section 1 — Company Profile
// ---------------------------------------------------------------------------

const Section1CompanyProfile = ({ companyData }) => {
  const {
    companyName,
    taxIdNumber,
    country,
    state,
    cityProvince,
    streetAddress,
    postalZipCode,
    yearOfIncorporation,
    noOfEmployees,
    noOfCustomers,
    websiteLink,
    applicantName,
    applicantDesignation,
    applicantEmail,
    signatoryName,
    signatoryDesignation,
    signatoryEmail,
  } = companyData;

  return (
    <View>
      <SectionHeader>1. Company Profile</SectionHeader>

      <DataRow label="Company Name" value={companyName} />
      <DataRow label="Tax ID Number" value={taxIdNumber} />
      <DataRow label="Country" value={country} />
      <DataRow label="State" value={state} />
      <DataRow label="City / Province" value={cityProvince} />
      <DataRow label="Street Address" value={streetAddress} />
      <DataRow label="Postal / ZIP Code" value={postalZipCode} />
      <DataRow label="Year of Incorporation" value={yearOfIncorporation} />
      <DataRow label="No. of Employees" value={noOfEmployees} />
      <DataRow label="No. of Customers" value={noOfCustomers} />
      <DataRow label="Website" value={websiteLink} />

      <SubSectionHeader>Applicant Details</SubSectionHeader>
      <DataRow label="Name" value={applicantName} />
      <DataRow label="Designation" value={applicantDesignation} />
      <DataRow label="Email" value={applicantEmail} />

      <SubSectionHeader>Signatory Details</SubSectionHeader>
      <DataRow label="Name" value={signatoryName} />
      <DataRow label="Designation" value={signatoryDesignation} />
      <DataRow label="Email" value={signatoryEmail} />
    </View>
  );
};

// ---------------------------------------------------------------------------
// Section 2 — Finance
// ---------------------------------------------------------------------------

// 2.1 Financial Information
const FinancialInformation = ({
  companyData,
  financeQuestions,
  financeSubQuestions,
  financeAnswers,
}) => {
  // Helper to get answer for a specific subQuestionId
  const getAnsBySubQId = (subQId) => {
    const ans = financeAnswers.find((a) => a.subQuestionId === subQId);
    return (ans && (ans.descriptionAnswer || ans.booleanAnswer?.toString())) || "—";
  };

  const currencyAnswer = getAnsBySubQId(10);
  const scaleAnswer = getAnsBySubQId(11);

  const isTradeRef = financeAnswers.some((a) => a.questionId === 18);

  if (isTradeRef) {
    const q18 = financeQuestions.find((q) => q.questionId === 18);
    const q19 = financeQuestions.find((q) => q.questionId === 19);

    const subQs18 = financeSubQuestions
      .filter((sq) => sq.questionId === 18 && !sq.answerTypeFileUpload)
      .sort((a, b) => (a.subQuestionId || 0) - (b.subQuestionId || 0));

    const subQs19 = financeSubQuestions
      .filter((sq) => sq.questionId === 19 && !sq.answerTypeFileUpload)
      .sort((a, b) => (a.subQuestionId || 0) - (b.subQuestionId || 0));

    const getAns = (subQId) => {
      const ans = financeAnswers.find((a) => a.subQuestionId === subQId);
      return (ans && (ans.descriptionAnswer || ans.booleanAnswer?.toString())) || "—";
    };

    return (
      <View>
        <SubSectionHeader>2.1 Financial Information</SubSectionHeader>

        {/* 2.1.1 Currency */}
        <SubSubSectionHeader>2.1.1 Select your Currency</SubSubSectionHeader>
        <DataRow label="Currency" value={currencyAnswer} />

        {/* 2.1.2 Scale */}
        <SubSubSectionHeader>2.1.2 Select your Scale</SubSubSectionHeader>
        <DataRow label="Scale" value={scaleAnswer} />

        <Text
          style={{
            color: "#555",
            fontSize: 8,
            marginTop: 6,
            marginBottom: 4,
          }}
        >
          Note: This company submitted trade reference details as financial
          information.
        </Text>
        {q18 && (
          <Text
            style={{
              color: "#555",
              fontSize: 8,
              marginBottom: 6,
            }}
          >
            {q18.questionHeading}
          </Text>
        )}

        <SubSubSectionHeader>2.1.3 Reference Company 1</SubSubSectionHeader>
        {subQs18.map((sq) => (
          <DataRow
            key={sq.subQuestionId}
            label={sq.answerDescription}
            value={getAns(sq.subQuestionId)}
          />
        ))}

        {q19 && (
          <>
            <SubSubSectionHeader>2.1.4 Reference Company 2</SubSubSectionHeader>
            {subQs19.map((sq) => (
              <DataRow
                key={sq.subQuestionId}
                label={sq.answerDescription}
                value={getAns(sq.subQuestionId)}
              />
            ))}
          </>
        )}
      </View>
    );
  }

  // Non-trade-reference mode: show Q17
  const q17 = financeQuestions.find((q) => q.questionId === 17);
  return (
    <View>
      <SubSectionHeader>2.1 Financial Information</SubSectionHeader>

      {/* 2.1.1 Currency */}
      <SubSubSectionHeader>2.1.1 Select your Currency</SubSubSectionHeader>
      <DataRow label="Currency" value={currencyAnswer} />

      {/* 2.1.2 Scale */}
      <SubSubSectionHeader>2.1.2 Select your Scale</SubSubSectionHeader>
      <DataRow label="Scale" value={scaleAnswer} />

      {q17 && (
        <>
          {q17.questionHeading ? (
            <Text style={{ fontWeight: 700, fontSize: 9, marginTop: 6, marginBottom: 4 }}>
              {q17.questionHeading}
            </Text>
          ) : null}
          <Text style={{ fontSize: 9, marginBottom: 4, color: "#555" }}>
            {q17.questionDescription}
          </Text>
        </>
      )}
      <Text
        style={{ color: "#888", fontSize: 8, marginTop: 4 }}
      >
        Note: File submissions are excluded from this report.
      </Text>
    </View>
  );
};

// 2.2 Public Information
const PublicInformation = ({
  financeCategories,
  financeQuestions,
  financeSubQuestions,
  financeAnswers,
}) => {
  const publicCat = financeCategories.find(
    (c) => c.categoryName && c.categoryName.toLowerCase().includes("public")
  );

  if (!publicCat) return null;

  const publicQs = financeQuestions
    .filter((q) => q.categoryId === publicCat.categoryId)
    .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

  if (!publicQs.length) return null;

  const getTextAnswer = (questionId) => {
    const sqs = financeSubQuestions.filter((sq) => sq.questionId === questionId);
    for (const sq of sqs) {
      const ans = financeAnswers.find((a) => a.subQuestionId === sq.subQuestionId);
      if (ans && ans.descriptionAnswer) return ans.descriptionAnswer;
    }
    return "—";
  };

  return (
    <View>
      <SubSectionHeader>2.2 Public Information</SubSectionHeader>
      {publicQs.map((q, idx) => (
        <View key={q.questionId} style={{ marginBottom: 6 }}>
          <SubSubSectionHeader>{`2.2.${idx + 1} ${q.questionDescription}`}</SubSubSectionHeader>
          <Text style={{ fontSize: 9, color: "#444" }}>
            {getTextAnswer(q.questionId)}
          </Text>
        </View>
      ))}
    </View>
  );
};

// 2.3 Credit Score
const CreditScoreSection = ({ creditScoreData }) => {
  const { year1, year2, year3, ratios, currency } = creditScoreData;

  if (!ratios || ratios.length === 0) return null;

  const hasAllYears =
    year1 &&
    year2 &&
    year3 &&
    Object.keys(year1).length > 0 &&
    Object.keys(year2).length > 0 &&
    Object.keys(year3).length > 0;

  // Normalise exchange rates if USD
  let y1 = year1 ? { ...year1 } : {};
  let y2 = year2 ? { ...year2 } : {};
  let y3 = year3 ? { ...year3 } : {};

  if (currency === "US Dollar") {
    if (y1.exchangeRate !== undefined) y1.exchangeRate = 1;
    if (y2.exchangeRate !== undefined) y2.exchangeRate = 1;
    if (y3.exchangeRate !== undefined) y3.exchangeRate = 1;
  }

  const year1Dollars = Object.keys(y1).length ? convertToDollars(y1) : {};
  const year2Dollars = Object.keys(y2).length ? convertToDollars(y2) : {};
  const year3Dollars = Object.keys(y3).length ? convertToDollars(y3) : {};

  // Score helpers that work on the USD-converted data
  const ratioForYear = (cat, yearNum) => {
    const y = yearNum === 1 ? year1Dollars : yearNum === 2 ? year2Dollars : year3Dollars;
    if (!Object.keys(y).length) return "";
    return getRatioForYear(cat, y, ratios);
  };

  const ratioGrowthForYear = (yearNum) => {
    if (yearNum === 1) return "0";
    const yCurr = yearNum === 2 ? year2Dollars : year3Dollars;
    const yPrev = yearNum === 2 ? year1Dollars : year2Dollars;
    if (!Object.keys(yCurr).length || !Object.keys(yPrev).length) return "";
    return getRatioForYearGrowth("Revenue growth", yCurr, yPrev, ratios);
  };

  const totalScore = (yearNum) =>
    calcTotalCreditScore(year1Dollars, year2Dollars, year3Dollars, ratios, yearNum);
  const finalTotal = calcFinalTotalCreditScore(year1Dollars, year2Dollars, year3Dollars, ratios);
  const creditRating = (yearNum) => {
    const score = totalScore(yearNum);
    return calcCreditRating(score, ratios);
  };
  const finalRating = calcCreditRating(finalTotal, ratios);

  // Column widths for the big credit-score table (8 columns)
  const COL = {
    ratio: "32%",
    fin: "8%",
    score: "8%",
    final: "12%",
  };

  // Table row helper
  const CsRow = ({ label, fin1, fin2, fin3, sc1, sc2, sc3, fsc, bold }) => (
    <View style={[styles.tableRow]}>
      <Text style={[styles.tableCell, { width: COL.ratio, fontWeight: bold ? 700 : 400 }]}>
        {label}
      </Text>
      <Text style={[styles.tableCell, { width: COL.fin, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {fin1 !== undefined ? fin1 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.fin, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {fin2 !== undefined ? fin2 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.fin, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {fin3 !== undefined ? fin3 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.score, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {sc1 !== undefined ? sc1 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.score, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {sc2 !== undefined ? sc2 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.score, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {sc3 !== undefined ? sc3 : ""}
      </Text>
      <Text style={[styles.tableCell, { width: COL.final, textAlign: "right", fontWeight: bold ? 700 : 400 }]}>
        {fsc !== undefined ? fsc : ""}
      </Text>
    </View>
  );

  // Workings table
  const WRow = ({ label, curr, yearBefore, priorYear, current, usd }) => (
    <View style={styles.tableRow}>
      <Text style={[styles.tableCell, { width: "30%" }]}>{label}</Text>
      <Text style={[styles.tableCell, { width: "15%", textAlign: "center" }]}>
        {curr}
      </Text>
      <Text style={[styles.tableCell, { width: "18%", textAlign: "right" }]}>
        {usd ? (yearBefore !== "" ? `$ ${fmt(yearBefore)}` : "") : (yearBefore !== "" ? fmt(yearBefore.toString()) : "")}
      </Text>
      <Text style={[styles.tableCell, { width: "18%", textAlign: "right" }]}>
        {usd ? (priorYear !== "" ? `$ ${fmt(priorYear)}` : "") : (priorYear !== "" ? fmt(priorYear.toString()) : "")}
      </Text>
      <Text style={[styles.tableCell, { width: "19%", textAlign: "right" }]}>
        {usd ? (current !== "" ? `$ ${fmt(current)}` : "") : (current !== "" ? fmt(current.toString()) : "")}
      </Text>
    </View>
  );

  const workingsRows = [
    { key: "currentAssets", label: "Current Assets" },
    { key: "currentLiability", label: "Current Liability" },
    { key: "cash", label: "Cash" },
    { key: "investments", label: "Investments" },
    { key: "totalDebt", label: "Total Debt" },
    { key: "totalAssets", label: "Total Assets" },
    { key: "revenue", label: "Revenue" },
    { key: "profit", label: "Profit" },
  ];

  const getVal = (yearObj, key) =>
    yearObj && yearObj[key] !== undefined ? parseFloat(yearObj[key]) : "";

  return (
    <View>
      <SubSectionHeader>2.3 Credit Score</SubSectionHeader>

      {/* 2.3.1 Credit Score Checking */}
      {hasAllYears && (
        <View>
          <SubSubSectionHeader>2.3.1 Credit Score Checking</SubSubSectionHeader>

          {/* Table header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: COL.ratio }]}>
              Ratios
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: "24%", textAlign: "center" },
              ]}
            >
              Customer Financials
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: "24%", textAlign: "center" },
              ]}
            >
              Credit Score
            </Text>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: COL.final, textAlign: "center" },
              ]}
            >
              Financial Credit Score
            </Text>
          </View>
          {/* Sub-header row */}
          <View
            style={[
              styles.tableRow,
              { backgroundColor: "#2a4a70", paddingVertical: 2 },
            ]}
          >
            <Text style={[styles.tableHeaderCell, { width: COL.ratio }]} />
            <Text style={[styles.tableHeaderCell, { width: COL.fin, textAlign: "right" }]}>N-2</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.fin, textAlign: "right" }]}>N-1</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.fin, textAlign: "right" }]}>N</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.score, textAlign: "right" }]}>N-2</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.score, textAlign: "right" }]}>N-1</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.score, textAlign: "right" }]}>N</Text>
            <Text style={[styles.tableHeaderCell, { width: COL.final, textAlign: "right" }]} />
          </View>

          <CsRow
            label="Current ratio = Current Assets / Current Liability"
            fin1={fmt(calcCurrentRatio(year1Dollars).toString())}
            fin2={fmt(calcCurrentRatio(year2Dollars).toString())}
            fin3={fmt(calcCurrentRatio(year3Dollars).toString())}
            sc1={ratioForYear("Current ratio", 1)}
            sc2={ratioForYear("Current ratio", 2)}
            sc3={ratioForYear("Current ratio", 3)}
            fsc={ratioForYear("Current ratio", 3)}
          />
          <CsRow
            label="Cash ratio = (Cash + Investments) / Current Liability"
            fin1={fmt(calcCashRatio(year1Dollars).toString())}
            fin2={fmt(calcCashRatio(year2Dollars).toString())}
            fin3={fmt(calcCashRatio(year3Dollars).toString())}
            sc1={ratioForYear("Cash ratio", 1)}
            sc2={ratioForYear("Cash ratio", 2)}
            sc3={ratioForYear("Cash ratio", 3)}
            fsc={ratioForYear("Cash ratio", 3)}
          />
          <CsRow
            label="Working capital ratio = Current assets - Current liability"
            fin1={fmt(calcWorkingCapitalRatio(year1Dollars).toString())}
            fin2={fmt(calcWorkingCapitalRatio(year2Dollars).toString())}
            fin3={fmt(calcWorkingCapitalRatio(year3Dollars).toString())}
            sc1={ratioForYear("WC", 1)}
            sc2={ratioForYear("WC", 2)}
            sc3={ratioForYear("WC", 3)}
            fsc={ratioForYear("WC", 3)}
          />
          <CsRow
            label="Debt ratio = Total debt / Total assets"
            fin1={fmt(calcDebtRatio(year1Dollars).toString())}
            fin2={fmt(calcDebtRatio(year2Dollars).toString())}
            fin3={fmt(calcDebtRatio(year3Dollars).toString())}
            sc1={ratioForYear("Debt ratio", 1)}
            sc2={ratioForYear("Debt ratio", 2)}
            sc3={ratioForYear("Debt ratio", 3)}
            fsc={ratioForYear("Debt ratio", 3)}
          />
          <CsRow
            label="Revenue"
            fin1={fmt(getRevenue(year1Dollars).toString())}
            fin2={fmt(getRevenue(year2Dollars).toString())}
            fin3={fmt(getRevenue(year3Dollars).toString())}
            sc1={ratioForYear("Revenue", 1)}
            sc2={ratioForYear("Revenue", 2)}
            sc3={ratioForYear("Revenue", 3)}
            fsc={ratioForYear("Revenue", 3)}
          />
          <CsRow
            label="Revenue growth %"
            fin1={year1Dollars.revenue ? "0.00%" : ""}
            fin2={`${fmt(calcRevenueGrowth(year2Dollars, year1Dollars).toString())}%`}
            fin3={`${fmt(calcRevenueGrowth(year3Dollars, year2Dollars).toString())}%`}
            sc1="0"
            sc2={ratioGrowthForYear(2)}
            sc3={ratioGrowthForYear(3)}
            fsc={ratioGrowthForYear(3)}
          />
          <CsRow
            label="Profit"
            fin1={fmt(getProfit(year1Dollars).toString())}
            fin2={fmt(getProfit(year2Dollars).toString())}
            fin3={fmt(getProfit(year3Dollars).toString())}
          />
          <CsRow
            label="Current Asset YoY %"
            fin2={`${fmt(calcCurrentAssetYoY(year2Dollars, year1Dollars).toString())}%`}
            fin3={`${fmt(calcCurrentAssetYoY(year3Dollars, year2Dollars).toString())}%`}
          />
          <CsRow
            label="Current Liability YoY %"
            fin2={`${fmt(calcCurrentLiabilityYoY(year2Dollars, year1Dollars).toString())}%`}
            fin3={`${fmt(calcCurrentLiabilityYoY(year3Dollars, year2Dollars).toString())}%`}
          />
          {/* Empty separator row */}
          <View style={[styles.tableRow, { paddingVertical: 4 }]}>
            <Text style={{ width: "100%" }} />
          </View>
          <CsRow
            bold
            label="Total Credit Score"
            sc1={totalScore(1)}
            sc2={totalScore(2)}
            sc3={totalScore(3)}
            fsc={finalTotal}
          />
          <CsRow
            bold
            label="Credit Rating"
            sc1={creditRating(1)}
            sc2={creditRating(2)}
            sc3={creditRating(3)}
            fsc={finalRating}
          />
        </View>
      )}

      {/* 2.3.2 Workings for Ratios (native currency) */}
      <View style={{ marginTop: 10 }}>
        <SubSubSectionHeader>
          {`2.3.2 Workings for Ratios (${currency || ""})`}
        </SubSubSectionHeader>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Items</Text>
          <Text style={[styles.tableHeaderCell, { width: "15%", textAlign: "center" }]}>
            Currency
          </Text>
          <Text style={[styles.tableHeaderCell, { width: "18%", textAlign: "right" }]}>
            Year Before (N-2)
          </Text>
          <Text style={[styles.tableHeaderCell, { width: "18%", textAlign: "right" }]}>
            Prior Year (N-1)
          </Text>
          <Text style={[styles.tableHeaderCell, { width: "19%", textAlign: "right" }]}>
            Current (N)
          </Text>
        </View>
        {workingsRows.map((row) => (
          <WRow
            key={row.key}
            label={row.label}
            curr={currency}
            yearBefore={getVal(year1, row.key)}
            priorYear={getVal(year2, row.key)}
            current={getVal(year3, row.key)}
          />
        ))}
        {currency !== "US Dollar" && (
          <WRow
            label="Exchange Rate"
            curr=""
            yearBefore={getVal(year1, "exchangeRate")}
            priorYear={getVal(year2, "exchangeRate")}
            current={getVal(year3, "exchangeRate")}
          />
        )}
      </View>

      {/* 2.3.2 Workings for Ratios (US Dollar) — only when not already USD */}
      {currency !== "US Dollar" && (
        <View style={{ marginTop: 10 }}>
          <SubSubSectionHeader>
            2.3.2 Workings for Ratios (US Dollar)
          </SubSubSectionHeader>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Items</Text>
            <Text
              style={[styles.tableHeaderCell, { width: "15%", textAlign: "center" }]}
            >
              Currency
            </Text>
            <Text
              style={[styles.tableHeaderCell, { width: "18%", textAlign: "right" }]}
            >
              Year Before (N-2)
            </Text>
            <Text
              style={[styles.tableHeaderCell, { width: "18%", textAlign: "right" }]}
            >
              Prior Year (N-1)
            </Text>
            <Text
              style={[styles.tableHeaderCell, { width: "19%", textAlign: "right" }]}
            >
              Current (N)
            </Text>
          </View>
          {workingsRows.map((row) => (
            <WRow
              key={row.key}
              label={row.label}
              curr="USD"
              yearBefore={getVal(year1Dollars, row.key)}
              priorYear={getVal(year2Dollars, row.key)}
              current={getVal(year3Dollars, row.key)}
              usd
            />
          ))}
        </View>
      )}
    </View>
  );
};

// 2.4 Finance Opinion
const FinanceOpinion = ({ financeComments, approvalData, financeQuestions, financeSubQuestions, financeAnswers }) => {
  const { financeResult, financeSpecialApproval } = approvalData || {};

  const creatorComments = financeComments.filter((c) => c.questionId === 42);
  const reviewerComments = financeComments.filter((c) => c.questionId === 43);

  // 2.4.4 — special approval is shown whenever financeSpecialApproval is set.
  // Status comes from approvalData.financeSpecialApproval ("requested"→Pending, "approved", "rejected").
  // Comment comes from text description answers in Q79 sub-questions (sub-question 98 is the boolean decision).
  const hasSpecialApproverRequest = !!financeSpecialApproval;
  const specialApproverStatus = normalizeStatus(financeSpecialApproval) || "Pending";

  const specialApproverComment = (() => {
    if (!financeAnswers) return null;
    const q79Answers = financeAnswers.filter((a) => a.questionId === 79 && a.subQuestionId !== 98);
    for (const a of q79Answers) {
      if (a.descriptionAnswer && a.descriptionAnswer.trim()) return a.descriptionAnswer.trim();
    }
    return null;
  })();

  return (
    <View>
      <SubSectionHeader>2.4 Finance Opinion</SubSectionHeader>

      <SubSubSectionHeader>2.4.1 Creator</SubSubSectionHeader>
      {creatorComments.length > 0 ? (
        creatorComments.map((item) => (
          <CommentCard key={item.commentId} item={item} />
        ))
      ) : (
        <Text style={{ fontSize: 8, color: "#888", marginBottom: 4 }}>
          No creator opinion submitted.
        </Text>
      )}

      <SubSubSectionHeader>2.4.2 Reviewer</SubSubSectionHeader>
      {reviewerComments.length > 0 ? (
        reviewerComments.map((item) => (
          <CommentCard key={item.commentId} item={item} />
        ))
      ) : (
        <Text style={{ fontSize: 8, color: "#888", marginBottom: 4 }}>
          No reviewer opinion submitted.
        </Text>
      )}

      <SubSubSectionHeader>2.4.3 Approver</SubSubSectionHeader>
      {financeResult ? (
        <View>
          <Text
            style={{ fontSize: 9, fontWeight: 700, color: statusColor(financeResult) }}
          >
            {normalizeStatus(financeResult)}
          </Text>
          {financeSpecialApproval ? (
            <Text style={{ fontSize: 8, color: "#444", marginTop: 3 }}>
              {financeSpecialApproval}
            </Text>
          ) : null}
        </View>
      ) : (
        <Text style={{ fontSize: 8, color: "#888" }}>Pending</Text>
      )}

      {/* 2.4.4 Special Approver — only shown when special approval has been requested */}
      {hasSpecialApproverRequest && (
        <View>
          <SubSubSectionHeader>2.4.4 Special Approver</SubSubSectionHeader>
          <Text style={{ fontSize: 9, fontWeight: 700, color: statusColor(specialApproverStatus) }}>
            {specialApproverStatus}
          </Text>
          {specialApproverComment ? (
            <Text style={{ fontSize: 8, color: "#444", marginTop: 3 }}>
              {specialApproverComment}
            </Text>
          ) : null}
        </View>
      )}

      <Text
        style={{
          fontSize: 7,
          color: "#888",
          marginTop: 6,
        }}
      >
        * File attachments are excluded from this report.
      </Text>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Section 3 — Legal
// ---------------------------------------------------------------------------

const Section3Legal = ({
  legalQuestions,
  legalSubQuestions,
  legalAnswers,
  legalComments,
  approvalData,
}) => {
  const { legalResult } = approvalData || {};

  const legalInfoQuestions = legalQuestions
    .filter((q) => q.categoryId === 2)
    .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

  const getSubQAnswer = (sq) => {
    const ans = legalAnswers.find((a) => a.subQuestionId === sq.subQuestionId);
    if (!ans) return "—";
    if (sq.answerType) {
      // boolean
      if (ans.booleanAnswer === true || ans.booleanAnswer === "true") {
        return ans.descriptionAnswer || "Yes";
      }
      return "No";
    }
    return ans.descriptionAnswer || "—";
  };

  return (
    <View>
      <SectionHeader>3. Legal</SectionHeader>

      <SubSectionHeader>3.1 Legal Information</SubSectionHeader>
      {legalInfoQuestions.map((q, idx) => {
        const subQs = legalSubQuestions
          .filter((sq) => sq.questionId === q.questionId && !sq.answerTypeFileUpload)
          .sort((a, b) => (a.subQuestionId || 0) - (b.subQuestionId || 0));

        return (
          <View key={q.questionId} style={{ marginBottom: 8 }}>
            <Text style={{ fontWeight: 700, fontSize: 9, marginBottom: 3 }}>
              {q.questionDescription}
            </Text>
            {subQs.map((sq) => (
              <View
                key={sq.subQuestionId}
                style={{
                  flexDirection: "row",
                  marginLeft: 12,
                  marginBottom: 3,
                }}
              >
                <Text
                  style={{
                    width: "45%",
                    color: "#666",
                    fontSize: 8,
                  }}
                >
                  {sq.answerDescription}
                </Text>
                <Text style={{ width: "55%", fontSize: 8, color: "#333" }}>
                  {getSubQAnswer(sq)}
                </Text>
              </View>
            ))}
          </View>
        );
      })}

      <SubSectionHeader>3.2 Legal Opinion</SubSectionHeader>

      <SubSubSectionHeader>3.2.1 Legal Comments</SubSubSectionHeader>
      {legalComments && legalComments.length > 0 ? (
        legalComments.map((item) => (
          <CommentCard key={item.commentId} item={item} />
        ))
      ) : (
        <Text style={{ fontSize: 8, color: "#888", marginBottom: 4 }}>
          No legal comments submitted.
        </Text>
      )}
      <Text
        style={{
          fontSize: 7,
          color: "#888",
          marginTop: 4,
          marginBottom: 6,
        }}
      >
        * File attachments are excluded from this report.
      </Text>

      <SubSubSectionHeader>3.2.2 Legal Approval</SubSubSectionHeader>
      {legalResult ? (
        <Text style={{ fontSize: 9, fontWeight: 700, color: statusColor(legalResult) }}>
          {normalizeStatus(legalResult)}
        </Text>
      ) : (
        <Text style={{ fontSize: 8, color: "#888" }}>Pending</Text>
      )}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Section 4 — Approval Summary
// ---------------------------------------------------------------------------

const Section4ApprovalSummary = ({ approvalData }) => {
  const { financeResult, financeSpecialApproval, legalResult, resellerStatus } = approvalData || {};

  const hasSpecialApproverRequest = !!financeSpecialApproval;
  const specialApprovalStatus = normalizeStatus(financeSpecialApproval) || "Pending";

  return (
    <View>
      <SectionHeader>4. Approval Summary</SectionHeader>
      <View style={styles.dataRow}>
        <Text style={styles.dataLabel}>Finance Approval</Text>
        <Text style={[styles.dataValue, { color: statusColor(financeResult), fontWeight: 700 }]}>
          {normalizeStatus(financeResult) || "Pending"}
        </Text>
      </View>
      {hasSpecialApproverRequest && (
        <View style={styles.dataRow}>
          <Text style={styles.dataLabel}>Finance Special Approval</Text>
          <Text style={[styles.dataValue, { color: statusColor(specialApprovalStatus), fontWeight: 700 }]}>
            {specialApprovalStatus}
          </Text>
        </View>
      )}
      <View style={styles.dataRow}>
        <Text style={styles.dataLabel}>Legal Approval</Text>
        <Text style={[styles.dataValue, { color: statusColor(legalResult), fontWeight: 700 }]}>
          {normalizeStatus(legalResult) || "Pending"}
        </Text>
      </View>
      <View style={styles.dataRow}>
        <Text style={styles.dataLabel}>Company Status</Text>
        <Text style={[styles.dataValue, { color: statusColor(resellerStatus), fontWeight: 700 }]}>
          {resellerStatus || "—"}
        </Text>
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Root document component
// ---------------------------------------------------------------------------

const AdminPDFReport = ({
  companyData = {},
  financeCategories = [],
  financeQuestions = [],
  financeSubQuestions = [],
  financeAnswers = [],
  financeComments = [],
  creditScoreData = { year1: {}, year2: {}, year3: {}, ratios: [], currency: "" },
  legalQuestions = [],
  legalSubQuestions = [],
  legalAnswers = [],
  legalComments = [],
  approvalData = {},
  reportDate = "",
}) => {
  const companyName = companyData.companyName || "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <PageHeader companyName={companyName} reportDate={reportDate} />
        <PageFooter />

        {/* Section 1 */}
        <Section1CompanyProfile companyData={companyData} />

        {/* Section 2 */}
        <View break />
        <SectionHeader>2. Finance</SectionHeader>

        <FinancialInformation
          companyData={companyData}
          financeQuestions={financeQuestions}
          financeSubQuestions={financeSubQuestions}
          financeAnswers={financeAnswers}
        />

        <PublicInformation
          financeCategories={financeCategories}
          financeQuestions={financeQuestions}
          financeSubQuestions={financeSubQuestions}
          financeAnswers={financeAnswers}
        />

        <CreditScoreSection creditScoreData={creditScoreData} />

        <FinanceOpinion
          financeComments={financeComments}
          approvalData={approvalData}
          financeQuestions={financeQuestions}
          financeSubQuestions={financeSubQuestions}
          financeAnswers={financeAnswers}
        />

        {/* Section 3 */}
        <View break />
        <Section3Legal
          legalQuestions={legalQuestions}
          legalSubQuestions={legalSubQuestions}
          legalAnswers={legalAnswers}
          legalComments={legalComments}
          approvalData={approvalData}
        />

        {/* Section 4 */}
        <View break />
        <Section4ApprovalSummary approvalData={approvalData} />
      </Page>
    </Document>
  );
};

export default AdminPDFReport;
